import { guardExhaustive } from 'libs/end-to-end/src/generic';
import React from 'react';
import { KeyFactorsImpactGrade } from 'apps/sigma2-ui/src/components/key-factors/types';

export interface RadialImpactChartArcProps {
  // This is the point that the first line segment starts.
  startX: number;
  startY: number;

  // The center point of the ring which you are drawing a line towards.
  ringCenterX: number;
  ringCenterY: number;

  // The radius of the ring which you are drawing a line towards.
  ringRadius: number;

  // The index of the ring you are drawing
  ringIndex: number;
  ringCount: number;

  // This is the radius of the outermost ring, and you need it to properly determine where the bend point in the line should occur.
  outermostRingRadius: number;

  // This controls how big the SVG renders so that it scales properly to DOM space.
  parentWidth: number;
  parentHeight: number;

  isHovered: boolean;
  grade: KeyFactorsImpactGrade;
}

const calculatePointOnCircle = ({
  pointAngle,
  centerX,
  centerY,
  radius,
}: {
  pointAngle: number;
  centerX: number;
  centerY: number;
  radius: number;
}): [number, number] => {
  const pointX = centerX + radius * Math.cos((pointAngle * Math.PI) / 180) * -1;
  const pointY = centerY + radius * Math.sin((pointAngle * Math.PI) / 180);

  return [pointX, pointY];
};

export const RadialImpactChartArc: React.FC<RadialImpactChartArcProps> = (
  props,
) => {
  // These angles are pointAngle values that we interpolate between.
  const startAngle = 330;
  const endAngle = 360 + (360 - startAngle);

  // This is the amount that each ring must interpolate, in degrees, to ensure that it provides a nice illusion.
  const angleLeap = (endAngle - startAngle) / Math.max(1, props.ringCount - 1);
  const pointAngle = startAngle + props.ringIndex * angleLeap;

  // First get the point on the ring itself.
  const [pointX, pointY] = calculatePointOnCircle({
    pointAngle,
    centerX: props.ringCenterX,
    centerY: props.ringCenterY,
    radius: props.ringRadius,
  });

  // We project a point on an imaginary outer-most ring, that always lives 10% outside.
  // This becomes the X for the bend-start point. We discard the Y because the Y for the bend point is going to match startX.
  const [checkpointX, _] = calculatePointOnCircle({
    pointAngle,
    centerX: props.ringCenterX,
    centerY: props.ringCenterY,
    radius: props.outermostRingRadius * 1.05,
  });

  const getColor = (): string => {
    switch (props.grade) {
      case 'high':
        return props.isHovered ? '#1C47CA' : '#537AEE';
      case 'medium':
        return props.isHovered ? '#791FD1' : '#A95FF1';
      case 'low':
        return props.isHovered ? '#52575B' : '#9DA0A3';
      default:
        throw guardExhaustive(props.grade);
    }
  };

  return (
    <svg
      width={props.parentWidth}
      height={props.parentHeight}
      viewBox={`0 0 ${props.parentWidth} ${props.parentHeight}`}
      pointerEvents={'none'}
      data-testid={`radial-impact-chart-arc-${props.ringIndex}`}
    >
      <g>
        <path
          d={`M${props.startX} ${props.startY} L${checkpointX} ${props.startY}L${pointX} ${pointY}`}
          stroke={getColor()}
          strokeWidth="1"
          fill="none"
        />
        <circle cx={pointX} cy={pointY} r="2" fill={getColor()} />
      </g>
    </svg>
  );
};
